import context from '@truckdown/systems';
import { AccountSummaryModel, AccountStatus, AccountType, AccountCommentModel, IAccountData } from "@truckdown/accounts";
import { AdminTaskStatus } from "@truckdown/tasks/AdminTaskStatus";
import { TaskFinder } from "@truckdown/tasks/TaskFinder";
import { defineComponent, ref, Ref, onMounted, onUnmounted, computed } from 'vue';
import editAccountHeader from './EditAccountHeader/EditAccountHeader.vue';

enum DisplayType {
    Vendor = 0,
    Fleet = 1
}

interface IData {
    accountSummary: Ref<AccountSummaryModel>,
    accountTypeForDisplay: Ref<AccountType>,
    showSummary: Ref<boolean>,
    showTaskCreator: Ref<boolean>,
    showMoreDropdown: Ref<boolean>,
    showMenu: Ref<string>,
    screenWidth: Ref<number>,
    headerElement: Ref<HTMLDivElement | null>,
    taskCount: Ref<number>,
    showVerifyModal: Ref<boolean>,
    isLoading: Ref<boolean>,
    showEditAccountHeader: Ref<boolean>
}

const getData = function (props: any) {
    return {
        accountSummary: ref<AccountSummaryModel>(props.accountSummary),
        accountTypeForDisplay: ref<AccountType>(props.accountSummary.accountType),
        showSummary: ref<boolean>(false),
        showTaskCreator: ref<boolean>(false),
        showMoreDropdown: ref<boolean>(false),
        showMenu: ref<string>(''),
        screenWidth: ref<number>(window.innerWidth),
        headerElement: ref<HTMLDivElement | null>(null),
        taskCount: ref<number>(0),
        showVerifyModal: ref<boolean>(false),
        isLoading: ref<boolean>(false),
        showEditAccountHeader: ref<boolean>(false)
    } as IData;
}

const getComputed = function (data: IData) {
    return {
        subscriptionType: computed(() => {
            return data.accountSummary.value.isPaid ? "Paid" : "Free";
        }),
        accountStatus: computed(() => {
            return AccountStatus[data.accountSummary.value.status];
        }),
        accountType: computed(() => {
            if (AccountType[data.accountSummary.value.accountType] == "CallCenter") {
                return "Call Center"
            } else {
                return AccountType[data.accountSummary.value.accountType];
            }
        }),
        emailLink: computed(() => {
            return '/emails-admin/edit-email?accountId=' + encodeURIComponent(data.accountSummary.value.id)
                + '&returnUrl=' + encodeURIComponent(window.location.href);
        }),
        tasksLink: computed(() => {
            return '/accounts-admin/view-account/tasks/' + data.accountSummary.value.id;
        })
    }
}

const getMethods = function (props: any, data: IData) {
    return {
        changeDisplay(evt: Event) {
            context.events.emit(
                'set-accounttype-fordisplay',
                data.accountTypeForDisplay.value);
        },
        dateDisplay: function (dateUTC: Date | string): Date | string | undefined {
            if (dateUTC)
                return new Date(dateUTC).toLocaleString();
            else
                return undefined;
        },
        completeTaskCreator: function (evt: Event) {
            if (data.showTaskCreator.value) {
                context.events.emit('refresh-tasks-data');
            }
            data.showTaskCreator.value = !data.showTaskCreator.value;
        },
        toggleTaskCreator: function (evt: Event) {
            data.showTaskCreator.value = !data.showTaskCreator.value;
        },
        routeTo: function (url: string) {
            window.location.href = url;
        },
        toggleShowMore: function () {
            data.showMoreDropdown.value = !data.showMoreDropdown.value;
        },
        reloadAccountHeader: async function () {
            if (!data.isLoading.value) {
                data.isLoading.value = true;
                data.showEditAccountHeader.value = false;

                try {
                    let accountData = await context.getService<IAccountData>('IAccountData');
                    var response = await accountData.getAccountSummary(data.accountSummary.value.ain);

                    if (response && response != null) {
                        if (data.accountSummary.value.accountType != response.accountType) {
                            window.location.href = window.location.pathname + window.location.search + window.location.hash;
                        } else {
                            data.accountSummary.value = response;
                        }
                    } else {
                        window.location.href = window.location.pathname + window.location.search + window.location.hash;
                    }
                } catch (err) {
                    window.location.href = window.location.pathname + window.location.search + window.location.hash;
                } finally {
                    data.isLoading.value = false;
                }
            }
        },
        cancelEditAccountHeader: function () {
            data.showEditAccountHeader.value = false;
        }
    }
}

const loadTasksData = async function (data: IData, props: any) {
    const findModel = {
        accountId: props.accountSummary?.id,
        statuses: [AdminTaskStatus.New, AdminTaskStatus.InProgress]
    }
    const tasksData = new TaskFinder(context);
    const result = await tasksData.getCount(findModel);
    data.taskCount.value = result.data ?? data.taskCount.value;
}

export default defineComponent({
    name: "AccountHeader",
    props: {
        accountSummary: {
            type: Object as () => AccountSummaryModel,
            required: true
        }
    },
    components: {
        'edit-account-header': editAccountHeader
    },
    setup: function (props, ctx: any) {
        const data = getData(props);
        const computed = getComputed(data);
        const methods = getMethods(props, data);

        const update_account_comments = (updatedComments: AccountCommentModel[]) => {
            data.accountSummary.value.comments = updatedComments;
        }

        const handleResize = () => {
            if (data.headerElement.value) {
                data.screenWidth.value = data.headerElement.value.offsetWidth;
            }
        };

        const refesh_tasks = () => {
            loadTasksData(data, props);
        };

        const close_verify_account = () => {
            data.showVerifyModal.value = false;
        };
         
        onMounted(async () => {
            context.events.on('update-account-comments', update_account_comments);
            context.events.on('refresh-tasks-data', refesh_tasks);
            window.addEventListener('resize', handleResize);
            context.events.on('close-verify-account', close_verify_account);
            await loadTasksData(data, props);
        });

        onUnmounted(() => {
            context.events.off('update-account-comments', update_account_comments);
            context.events.off('refresh-tasks-data', refesh_tasks);
            context.events.off('close-verify-account', close_verify_account);
            window.removeEventListener('resize', handleResize);
        });

        return {
            ...data,
            ...methods,
            ...computed,
            'AccountType': AccountType,
            'DisplayType': DisplayType
        };
    }
});
